import { getDesktopBridge, isDesktopPlatform } from '@/lib/desktop/bridge';

import { wipeOfflineDb } from './offline-db/connection';
import { isNativePlatform } from './platform';
import { getSupabaseAuthStorage } from './secure-storage-adapter';

import type { Session, SupabaseClient, User } from '@supabase/supabase-js';

/**
 * Legacy plaintext keys written by the pre-Keychain auth-persistence layer
 * (before this PR). Must be removed on logout / clearAllAppData so devices
 * upgrading from an older build don't keep a plaintext refresh token
 * sitting in iOS UserDefaults.
 *
 * Safe to keep this list narrow: only the two keys the old build wrote.
 * Wholesale `Preferences.clear()` would wipe legitimate non-auth prefs
 * (e.g. UI state) which the new code paths do still use.
 */
const LEGACY_PREFERENCES_KEYS = ['supabase_session', 'session_revoked_at'] as const;

interface LegacyStoredSession {
  currentSession?: Session;
  session?: Session;
  access_token?: string;
  refresh_token?: string;
  user?: User;
}

type SessionWithTokens = Session & {
  access_token: string;
  refresh_token: string;
};

export type NativeAuthSessionRead =
  | { readonly status: 'available'; readonly session: Session | null }
  | { readonly status: 'unavailable' };

/** Storage key the Supabase client persists its auth session under (`sb-<ref>-auth-token`). */
export function getSupabaseAuthTokenKey(url: string): string {
  try {
    const { hostname } = new URL(url);
    const projectId = hostname.split('.')[0];
    return `sb-${projectId}-auth-token`;
  } catch {
    throw new Error('Invalid Supabase URL');
  }
}

function hasSessionTokens(session: Session | null | undefined): session is SessionWithTokens {
  return Boolean(session?.access_token && session.refresh_token);
}

function readLocalStorageSession(): Session | null {
  try {
    const storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
    const storedSession = globalThis.localStorage.getItem(storageKey);
    if (!storedSession) {
      return null;
    }

    const parsed = JSON.parse(storedSession) as LegacyStoredSession;
    if (parsed.currentSession) {
      return parsed.currentSession;
    }
    if (parsed.session) {
      return parsed.session;
    }
    if (parsed.access_token && parsed.refresh_token && parsed.user) {
      return parsed as unknown as Session;
    }
  } catch {
    console.error('[AuthPersistence] Failed to parse stored auth session');
  }
  return null;
}

function parseStoredSession(value: string | null): Session | null {
  if (!value) {
    return null;
  }
  try {
    const parsed = JSON.parse(value) as LegacyStoredSession;
    const candidate = parsed.currentSession ?? parsed.session ?? parsed;
    return hasSessionTokens(candidate as Session) && typeof candidate.user?.id === 'string'
      ? (candidate as Session)
      : null;
  } catch {
    return null;
  }
}

/**
 * Read the native session directly from the Keychain without asking auth-js to
 * refresh it. An expired access token still proves the local account identity;
 * server access remains impossible until auth-js refreshes after reconnect.
 */
export async function readNativeAuthSessionState(): Promise<NativeAuthSessionRead> {
  if (!isNativePlatform()) {
    return { status: 'available', session: null };
  }

  try {
    const storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
    const value = await getSupabaseAuthStorage().getItem(storageKey);
    return { status: 'available', session: parseStoredSession(value) };
  } catch {
    console.error('[AuthPersistence] Failed to read the native auth session');
    return { status: 'unavailable' };
  }
}

export async function getInitialNativeAuthSession(): Promise<Session | null> {
  const result = await readNativeAuthSessionState();
  return result.status === 'available' ? result.session : null;
}

export function getInitialBrowserAuthSession(): Session | null {
  if (isNativePlatform() || isDesktopPlatform()) {
    return null;
  }

  return readLocalStorageSession();
}

/**
 * Removes the plaintext `localStorage` copy of the Supabase auth session.
 *
 * On desktop and native the session of record lives in the Keychain, so any
 * copy of it sitting in `localStorage` is legacy material with a refresh token
 * in it — readable off disk long after the session it belongs to ended.
 */
function clearPlaintextAuthStorage(): void {
  try {
    const storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
    globalThis.localStorage.removeItem(storageKey);
    globalThis.localStorage.removeItem(`${storageKey}-code-verifier`);
    globalThis.localStorage.removeItem(`${storageKey}-user`);
  } catch {
    console.error('[AuthPersistence] Failed to clear plaintext auth storage');
  }
}

export async function ensureSupabaseAuthSession(
  supabase: SupabaseClient,
  fallbackSession: Session | null | undefined,
): Promise<boolean> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (!error && session?.access_token) {
    return true;
  }

  if (!hasSessionTokens(fallbackSession)) {
    return false;
  }

  const { error: setSessionError } = await supabase.auth.setSession({
    access_token: fallbackSession.access_token,
    refresh_token: fallbackSession.refresh_token,
  });
  if (setSessionError) {
    return false;
  }

  const {
    data: { session: hydratedSession },
    error: hydratedError,
  } = await supabase.auth.getSession();

  return !hydratedError && Boolean(hydratedSession?.access_token);
}

async function clearLegacyPreferencesKeys(): Promise<void> {
  let Preferences: typeof import('@capacitor/preferences').Preferences;
  try {
    ({ Preferences } = await import('@capacitor/preferences'));
  } catch {
    // Plugin unavailable (web, misconfigured native) - nothing to purge.
    console.warn('[AuthPersistence] @capacitor/preferences unavailable; skipping legacy purge');
    return;
  }
  for (const key of LEGACY_PREFERENCES_KEYS) {
    try {
      await Preferences.remove({ key });
    } catch {
      // Best-effort cleanup. Don't block logout on a failed legacy purge.
      console.error('[AuthPersistence] Failed to clear legacy preferences key');
    }
  }
}

/**
 * Migrate native auth persistence on app boot: purge any plaintext refresh
 * token left in @capacitor/preferences by the pre-Keychain build. Without
 * this, a user who never logs out after upgrading keeps their old plaintext
 * Supabase session in UserDefaults forever (Copilot, PR #1776).
 *
 * Idempotent and best-effort - re-running is harmless. Safe to call from
 * the native app-boot path before the first auth refresh.
 */
export async function migrateLegacyAuthPersistence(): Promise<void> {
  if (isDesktopPlatform()) {
    clearPlaintextAuthStorage();
    return;
  }
  if (!isNativePlatform()) {
    return;
  }
  await clearLegacyPreferencesKeys();
}

/**
 * Move any plaintext `localStorage` session into the Keychain on native boot.
 *
 * The copy in `localStorage` is DELETED once the session is established, so
 * this is a migration rather than a duplication (R18) — leaving the source in
 * place would keep a refresh token readable off disk for the life of the
 * install. Deletion is also correct when the client already held a Keychain
 * session and never consulted the fallback: that makes the `localStorage` copy
 * pure residue.
 *
 * A FAILED hydrate deliberately retains it. It is the only remaining copy of
 * the session at that point, so discarding it would sign the user out for good
 * on a transient error; the next boot retries.
 */
export async function hydrateNativeSupabaseAuth(supabase: SupabaseClient): Promise<Session | null> {
  if (!isNativePlatform()) {
    return null;
  }

  const keychainSession = await getInitialNativeAuthSession();
  if (keychainSession) {
    clearPlaintextAuthStorage();
    return keychainSession;
  }

  const localStorageSession = readLocalStorageSession();
  const hydrated = await ensureSupabaseAuthSession(supabase, localStorageSession);
  if (hydrated) {
    clearPlaintextAuthStorage();
    return localStorageSession;
  }
  if (hasSessionTokens(localStorageSession)) {
    console.error('[AuthPersistence] Failed to hydrate native Supabase session');
  }
  return null;
}

/**
 * Removes the desktop shell's auth session, which lives behind the bridge's
 * `authStorage` (an OS keychain in the Electron main process), not in
 * `localStorage`. Best-effort per key: one rejection must not strand the rest.
 */
async function clearDesktopBridgeAuthStorage(
  authStorage: NonNullable<ReturnType<typeof getDesktopBridge>>['authStorage'],
): Promise<void> {
  let storageKey: string;
  try {
    storageKey = getSupabaseAuthTokenKey(import.meta.env.VITE_SUPABASE_URL);
  } catch {
    console.error('[AuthPersistence] Cannot derive the desktop auth storage key');
    return;
  }
  for (const key of [storageKey, `${storageKey}-code-verifier`, `${storageKey}-user`]) {
    try {
      await authStorage.removeItem(key);
    } catch {
      console.error('[AuthPersistence] Failed to clear a desktop auth storage key');
    }
  }
}

/**
 * Destroys every local trace of the session, on whichever shell is running.
 *
 * The single owner of auth-storage teardown: callers MUST NOT hand-roll a
 * `localStorage.removeItem` of the Supabase token key alongside it. Each shell
 * keeps the session of record somewhere different — `localStorage` on web, the
 * bridge's `authStorage` on desktop, the Keychain on native (see
 * `getSupabaseAuthStorage`) — and clearing only one of them is indistinguishable
 * from working on the platforms that use the others.
 *
 * The plaintext `localStorage` copy is cleared on EVERY platform, not just the
 * ones that treat it as the store of record: `-code-verifier` and `-user` sit
 * beside the token key, and the native migration path can leave a copy behind.
 */
export async function clearAllAppData(): Promise<void> {
  clearPlaintextAuthStorage();

  const desktopBridge = getDesktopBridge();
  if (desktopBridge) {
    await clearDesktopBridgeAuthStorage(desktopBridge.authStorage);
    return;
  }
  if (isDesktopPlatform() || !isNativePlatform()) {
    return;
  }

  // The encrypted offline cache dies with the session: key crypto-erased
  // first, then the file. Best-effort like every step here - wipeOfflineDb
  // swallows its own failures.
  await wipeOfflineDb().catch(() => null);

  try {
    const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');
    await SecureStorage.clear();
  } catch {
    console.error('[SecureStorage] Failed to clear app data');
  }

  // Belt-and-braces: also wipe any legacy plaintext session keys that
  // pre-Keychain builds may have left behind in UserDefaults.
  await clearLegacyPreferencesKeys();
}
